import { CartItem, ShopInfo, queryIsNewUser, BannerItem, EnumFlashSaleStatus } from '@/page/shoppingCar/views/api';
import { request } from '@kibt/krn-utils';
import yoda from '@kibt/bridge';
import { logger } from '@/common/log';
import { api, getEnv, getKrnDomain } from '@/common/api';
import { $k18n } from '@/common/i18n';
import { useToast } from '@/component/Toast/Toast';
import { openUrl } from '@/common/bridge/openUrl';
import queryString from 'query-string';
import { useSetState } from '@/hooks/useSetState';
import { useEffect, useState } from 'react';
import { http } from '@/request';
import { useQuery } from './hooks/useGetStoreCartItem';
import { useShopInfoStore } from './store/shopInfoStore';
import { useResumeOrFocused } from '@/hooks/useResumeOrFocused';
import { Toast } from '@kibt/meta-ui-krn';

export const limitSaleState = (item: CartItem) => {
    if (item?.limitSaleInfo?.limited) {
        const { timeOut, startTime, nowTime, endTime, saleOut } = item?.limitSaleInfo;
        // 售卖结束
        // 22年10月新增逻辑
        if (timeOut || saleOut) {
            return {
                state: 'TIMEOUT',
            };
        }
        // 即将售卖
        if (startTime && nowTime && nowTime < startTime) {
            return {
                state: 'COMESOON',
                count: startTime - nowTime,
            };
        }
        // 售卖中
        if (startTime && nowTime && endTime && nowTime > startTime && nowTime < endTime) {
            return {
                state: 'SALEING',
                count: endTime - nowTime,
            };
        }
        // 都不满足 则返回非限时售卖状态
        return {
            state: 'NORMALSALE',
        };
    }
    // 兜底 返回非限时售卖状态
    return {
        state: 'NORMALSALE',
    };
};

const hasSetAddonQuery = false;
export function useCardList(props: { source: 'recommendGoods' | 'shoppingCar'; addonQuery?: any }) {
    const [state, setState] = useSetState({
        items: [] as CartItem[],
        banners: [] as BannerItem[],
        couponList: [] as any[],
        shopInfo: null as ShopInfo,
        pageStatus: 'loading' as 'loading' | 'finish' | 'empty',
        couponInfo: {} as {
            batchId: number;
            bgColor: string;
            color: string;
            couponType: string;
            text: string;
        },
    });
    const { query, setQuery } = useQuery();

    const authorId = query.authorId;
    const liveId = query.liveId;

    const { showToast, toastNode } = useToast();

    const loadData = (
        query: {
            photoId?: number | string;
            liveId?: number | string;
            authorId: number | string;
            couponBatchId: number | string;
            itemId: number | string;
            belongMerchantId: number | string;
        },
        options: { init?: boolean; loading?: boolean } = { init: false, loading: false },
    ) => {
        if (options.loading) {
            setState({ pageStatus: 'loading' });
        }

        if (props.addonQuery && !hasSetAddonQuery) {
            setQuery(props.addonQuery);
        }

        const newQuery = useShopInfoStore.getState().query;
        const { photoId, liveId, merchantId, ...restParams } = newQuery;
        const params = {
            recommendGoods: { ...restParams, belongMerchantId: merchantId, photoId },
            shoppingCar: { ...restParams, belongMerchantId: merchantId, liveId },
        }[props.source];
        const apiPath = {
            recommendGoods: '/rest/o/ecommerce/customer/getCouponPhotoItems',
            shoppingCar: '/rest/o/ecommerce/customer/getCouponCartItems',
        }[props.source];

        return request.get(api(apiPath), { params }).then((res) => {
            setState({
                items: (res?.data?.cartItems || []).map((v, idx) => ({
                    ...v,
                    index: idx,
                    sellerMerchantId: res?.data?.sellerMerchantId,
                })), // 为每个小黄车商品添加索引 为了在调整顺序时候 左上角的索引值能正确显示
                banners: res?.extra?.banners || [],
                shopInfo: res?.data?.shopInfo || null,
                couponInfo: res?.data?.coupon,
                pageStatus: 'finish',
            });
        });
    };

    // 此处加购点击复用埋点，type=cart
    const gotoDetail = async (item: CartItem, type = 'detail', addType = 0) => {
        const saleState = limitSaleState(item).state;

        // 限时售卖商品 售罄状态 即将售卖 都不允许点击购买
        if (saleState === 'TIMEOUT' || saleState === 'COMESOON') {
            return;
        }

        // 秒杀活动抢光状态或者无活动时，商品库存为0，不允许点击唤起商详页
        if ((item.flashSaleStatus === EnumFlashSaleStatus.SnatchUp || item.flashSaleStatus === EnumFlashSaleStatus.NoSale) && item.inventory <= 0) {
            return;
        }

        logger.sendClick({
            action: 'SHOPPING_LIST',
            params: (() => {
                const params: any = {
                    live_id: liveId,
                    author_id: authorId,
                    cartitem_id: item.cartItemId,
                    index: item.index + 1,
                    sale_stats: (() => {
                        if (saleState === 'TIMEOUT') return 2;
                        if (saleState === 'SALEING') return 1;
                        if (saleState === 'COMESOON') return 0;
                        return undefined;
                    })(),
                };
                if (item.couponList?.[0]?.textType === 1) {
                    params.coupon_amt = item.couponList?.[0]?.couponAmt;
                    params.coupon_id = item.couponList?.[0]?.couponId;
                    params.coupon_type = item.couponList?.[0]?.couponType;
                }
                // 如果是售罄状态 埋点上报3
                if (item?.limitSaleInfo?.saleOut) {
                    params.sale_stats = 3;
                }
                // 区分加购和购买跳转详情页
                params.btn_type = type === 'cart' ? 1 : 0;
                params.add_type = addType; // 0-吊起规格页，1-加购成功
                return params;
            })(),
        });
        if (type === 'cart') {
            return;
        }
        // 实时统计用户点击行为
        // hitCommodity({ cartItemId: item.cartItemId, liveStreamId: liveId, source: 'cart' });

        // 表明是11月大促的特价商品
        if (item.specialCommodity) {
            try {
                const { data } = await queryIsNewUser({
                    authorId,
                    commodityId: item.commodityId,
                });
                // 只有新客才可以买特价商品
                if (data === false) {
                    Toast.show({ text: $k18n('折扣仅在首次下单时可用') });
                    logger.sendClick({
                        action: 'OLD_REFUSE_TOAST',
                    });
                    return;
                }
            } catch (e) {
                console.log('queryIsNewUser 接口挂了 但是不会影响下单跳转');
            }
        }

        // 复制优惠码
        if (item.code) {
            yoda.tool.setClipBoard({ text: item.code });
        }

        const urls = [
            // 如果是kwai的商品详情页，需要转成一码多投链接
            'm-shop.kwai.com/shop/c/detail',
            'm-shop.staging.kuaishou.com/shop/c/detail',
            'm-shop.prt.kuaishou.com/shop/c/detail',
            'm-shop.kwai.com/krn-web/detail',
            'm-shop.staging.kuaishou.com/krn-web/detail',
            'm-shop.prt.kuaishou.com/krn-web/detail',
        ];
        let url = item.jumpUrl;
        for (let i = 0; i < urls.length; i++) {
            if (url.includes(urls[i]) && !url.includes('redirectUrl')) {
                // 一码多投url
                const originQuery = queryString.parseUrl(url).query || {};
                const krnUrl = queryString.stringify(
                    {
                        bundleId: 'Detail',
                        componentName: 'Detail',
                        env: getEnv(),
                        cornerRadius: 16,
                        ...originQuery,
                        __launch_options__: null,
                        hyId: null,
                        height: 1, // iosrn 新增跳转参数
                        foldRatio: 0.75, // iosrn 新增跳转参数
                        openSlideFullScreen: 1, // iosrn 新增跳转参数 表明是可拖拽的半屏
                        isHalf: 1, // iosrn 新增跳转参数
                    },
                    { sort: false, skipNull: true },
                );
                url = queryString.stringifyUrl({
                    url: `${getKrnDomain()}/krn-web/detail`,
                    query: {
                        ...originQuery,
                        hyId: 'kibt-fe-shop-c-krn',
                        cornerRadius: 16,
                        redirectUrl: krnUrl,
                    },
                });
                break;
            }
        }
        // 后端下发的jumpUrl有时候走不到上面的if逻辑里，https://m-shop-krn.staging.kuaishou.com/krn-web/detail?cornerRadius=16&env=staging&__launch_options__=%7B%22topBarPosition%22%3A%22fixed%22%7D&itemId=3066441370&redirectUrl=bundleId%3DDetail%26componentName%3DDetail%26env%3Dstaging%26height%3D0.8h%26cornerRadius%3D16%26itemId%3D3066441370&abGroup=2&extParams=%7B%22cId%22%3A3066400370%2C%22cT%22%3A51%2C%22cS%22%3A%22%22%7D
        // 统一增加下面的场景参数
        url = queryString.stringifyUrl({
            url,
            query: {
                scene: '22',
                krnFollowParams: 1,
            },
        });
        openUrl({ url, halfWebview: false });
    };

    return {
        state,
        setState,
        showToast,
        toastNode,
        loadData,
        gotoDetail,
    };
}

export const useCartSkuCount = () => {
    // 购物车入口红点数量
    const [cartSkuCount, setCartSkuCount] = useState(undefined);

    const getCartSkuCount = async () => {
        try {
            const res = await http('GET/rest/o/ecommerce/customer/shopCart/state', {}, { toastError: false });
            if (res.result && res.result === 1) {
                setCartSkuCount(res.data.skuCount || 0);
            }
        } catch (e) {}
    };

    // server端购物车数量是异步的，加购成功不能直接刷新接口，修改为前端根据加购结果count+1
    const increase = () => {
        setCartSkuCount((v) => v + 1);
    };

    useResumeOrFocused(() => {
        getCartSkuCount();
    });

    useEffect(() => {
        getCartSkuCount();
    }, []);

    return {
        cartSkuCount,
        increase,
    };
};
